/**
 * Normed colour scale for the asset-page indicator band.
 *
 *   red = sell (0 % allocation) · neutral = hold · green = buy (100 %)
 *
 * The green leans teal on purpose. It is a *standard* green that breaks the
 * scale: red against #0ca30c measures ΔE 4.1 under deuteranopia, so the two
 * states that must never be confused are one colour for ~8 % of men — below
 * even the ΔE 6–8 floor that secondary encoding can rescue. Pulling the green
 * toward teal lifts that pair to ΔE 8.9 while still reading as green.
 *
 * With that pole in place the amber midpoint measures clean; the traffic-light
 * problem is the red/green pair, not the yellow.
 *
 * Measured (dataviz validate_palette.js, --pairs all):
 *   light  buy↔sell ΔE 8.9 protan · 22.1 normal   hold↔sell ΔE 22.1 normal
 *   dark   buy↔sell ΔE 9.7 deutan · 22.7 normal   contrast all >= 3:1
 *
 * Adjacent steps *within* the ramp sit close together by construction — that is
 * what a continuum is; the categorical adjacency check does not apply. What
 * matters is that each arm is monotone in lightness and the poles separate.
 *
 * Hold sits below 3:1 against the light surface (2.33), which is why the band
 * ships with a legend.
 */
import * as d3 from 'd3';

export interface IndicatorPalette {
    sell: string;
    hold: string;
    buy: string;
}

export const INDICATOR_PALETTE: Record<'light' | 'dark', IndicatorPalette> = {
    // A deeper amber than the usual signal yellow: at L 0.81 a bright yellow
    // all but vanishes on a light surface.
    light: { sell: '#d03b3b', hold: '#dd9b0e', buy: '#0d7a70' },
    dark: { sell: '#e05a5a', hold: '#e8b53a', buy: '#2aa79b' },
};

/**
 * Colours for the return/Sharpe figures in the series menu — green when a
 * number is good, red when it is bad.
 *
 * Deliberately separate steps from the band above: these are text and need
 * WCAG 4.5:1 at 11 px, which the band's mark colours do not all reach. Measured
 * against the menu surface: light 6.39:1 / 6.54:1, dark 7.46:1 / 6.14:1.
 *
 * The sign is already in the text (+2.5% / −13.1%), so colour reinforces rather
 * than carries the meaning — it is never the only channel.
 */
export const METRIC_TEXT_COLORS: Record<
    'light' | 'dark',
    { positive: string; negative: string }
> = {
    light: { positive: '#0a6b60', negative: '#b02a2a' },
    dark: { positive: '#4dbfb0', negative: '#ef7a7a' },
};

/** null for an unmeasured value, so the caller can fall back to a text token. */
export function metricTextColor(
    value: number | null,
    isDark: boolean
): string | null {
    if (value === null || !Number.isFinite(value) || value === 0) return null;
    const c = METRIC_TEXT_COLORS[isDark ? 'dark' : 'light'];
    return value > 0 ? c.positive : c.negative;
}

export function indicatorPalette(isDark: boolean): IndicatorPalette {
    return INDICATOR_PALETTE[isDark ? 'dark' : 'light'];
}

/**
 * Continuous scale over an allocation in [0, 1]. Lab interpolation keeps the
 * steps perceptually even, so the middle of the ramp does not bunch up.
 */
export function indicatorScale(isDark: boolean): (value: number) => string {
    const p = indicatorPalette(isDark);
    const scale = d3
        .scaleLinear<string>()
        .domain([0, 0.5, 1])
        .range([p.sell, p.hold, p.buy])
        .interpolate(d3.interpolateLab)
        .clamp(true);
    return (value: number) => scale(Number.isFinite(value) ? value : 0);
}

/** In/out series have no middle, so they land on the poles, never on hold. */
export function indicatorBinaryColor(value: number, isDark: boolean): string {
    const p = indicatorPalette(isDark);
    return value >= 0.5 ? p.buy : p.sell;
}
